/**
 * The calls of the platform administration (concept 12.7), through the backoffice's client.
 *
 * Same bundle, same origin rule, another API: the platform answers only on its own host,
 * names no action and has its own session. A 401 here therefore means "sign in at the
 * platform", and it is also how the API asks for a fresh sign-in (`reauth`) before a write
 * that cannot be undone. Both end in the same redirect.
 */

import type { components, CountdownApiClient } from "@countdown/api-client";

import { LOAD_FAILED, client, outcomeMessage, signInUrl } from "@/lib/api";

export type PlatformSession = components["schemas"]["PlatformSessionView"];
export type PlatformCell = components["schemas"]["PlatformCellView"];
export type PlatformControl = components["schemas"]["PlatformControlView"];
export type CellCreateRequest = components["schemas"]["CellCreateRequest"];
export type ProvisioningReport =
  components["schemas"]["ProvisioningReportView"];
export type MigrationResult = components["schemas"]["MigrationResultView"];
export type ControlMigration = components["schemas"]["ControlMigrationView"];
export type PlatformCellDetail =
  components["schemas"]["PlatformCellDetailView"];
export type DomainAddRequest = components["schemas"]["DomainAddRequest"];
export type PlatformStepOutcome = components["schemas"]["PlatformOutcomeView"];
export type IdentityRealm = components["schemas"]["IdentityRealmView"];
export type CellPolicy = components["schemas"]["CellPolicyView"];
export type CellPolicyRequest = components["schemas"]["CellPolicyRequest"];
export type Verification = components["schemas"]["VerificationView"];
export type SuppressionApplyRequest =
  components["schemas"]["SuppressionApplyRequest"];
export type SuppressionApplication =
  components["schemas"]["SuppressionApplyView"];
export type SeedResult = components["schemas"]["SeedView"];

export const PLATFORM_SIGN_IN_PATH = "/api/v1/platform/login";

export type PlatformOutcome<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly message: string };

export function loadPlatformSession(api: CountdownApiClient) {
  return api.GET("/api/v1/platform/session");
}

export function loadCells(api: CountdownApiClient) {
  return api.GET("/api/v1/platform/cells");
}

export function loadControl(api: CountdownApiClient) {
  return api.GET("/api/v1/platform/control");
}

export function loadCell(api: CountdownApiClient, slug: string) {
  return api.GET("/api/v1/platform/cells/{slug}", {
    params: { path: { slug } },
  });
}

/**
 * The only states a migration reaches (`CellProvisioner._migration_targets`); the other
 * cells have no revision worth reading and nothing to migrate.
 */
export function isMigratable(
  cell: Pick<PlatformCell, "provisioning_state">,
): boolean {
  return (
    cell.provisioning_state === "ready" ||
    cell.provisioning_state === "in_progress"
  );
}

/**
 * One cell's schema revision, or null when it could not be read.
 *
 * Per cell and not in the list, because reading it opens that cell's database: the list
 * stays fast when one cell does not answer, and only its row stays empty.
 */
export async function loadCellRevision(slug: string): Promise<string | null> {
  try {
    const { data, response } = await client.GET(
      "/api/v1/platform/cells/{slug}",
      { params: { path: { slug } } },
    );
    if (data === undefined) {
      console.error("[platform] cell read failed", slug, response.status);
      return null;
    }
    return data.current_revision;
  } catch (caught: unknown) {
    console.error("[platform] cell read threw", slug, caught);
    return null;
  }
}

async function write<T>(
  label: string,
  call: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<PlatformOutcome<T>> {
  try {
    const { data, error, response } = await call();
    if (data !== undefined) {
      return { ok: true, data };
    }
    console.error("[platform] write failed", label, response.status);
    if (response.status === 401) {
      window.location.assign(signInUrl(PLATFORM_SIGN_IN_PATH, window.location));
    }
    return { ok: false, message: outcomeMessage(response.status, error) };
  } catch (caught: unknown) {
    console.error("[platform] write threw", label, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export function createCell(
  body: CellCreateRequest,
): Promise<PlatformOutcome<ProvisioningReport>> {
  return write("create", () =>
    client.POST("/api/v1/platform/cells", { body }),
  );
}

export function migrateCell(
  slug: string,
): Promise<PlatformOutcome<MigrationResult>> {
  return write(`migrate ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/migrate", {
      params: { path: { slug } },
    }),
  );
}

export function migrateControl(): Promise<PlatformOutcome<ControlMigration>> {
  return write("control migrate", () =>
    client.POST("/api/v1/platform/control/migrate", {}),
  );
}

export function addDomain(
  slug: string,
  body: DomainAddRequest,
): Promise<PlatformOutcome<PlatformStepOutcome>> {
  return write(`domain ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/domains", {
      params: { path: { slug } },
      body,
    }),
  );
}

/** An empty name goes back to deriving the realm from the slug. */
export function setIdentityRealm(
  slug: string,
  name: string,
): Promise<PlatformOutcome<IdentityRealm>> {
  return write(`realm ${slug}`, () =>
    client.PUT("/api/v1/platform/cells/{slug}/identity-realm", {
      params: { path: { slug } },
      body: { name: name || null },
    }),
  );
}

/** All settings at once; a key left at its default is stored as nothing. */
export function setCellPolicy(
  slug: string,
  body: CellPolicyRequest,
): Promise<PlatformOutcome<CellPolicy>> {
  return write(`policy ${slug}`, () =>
    client.PUT("/api/v1/platform/cells/{slug}/policy", {
      params: { path: { slug } },
      body,
    }),
  );
}

/** One entry per line, blank lines and surrounding spaces dropped. */
export function lines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export function syncIdentityRealm(
  slug: string,
): Promise<PlatformOutcome<PlatformStepOutcome>> {
  return write(`realm sync ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/identity-realm/sync", {
      params: { path: { slug } },
    }),
  );
}

export function verifyCell(
  slug: string,
): Promise<PlatformOutcome<Verification>> {
  return write(`verify ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/verify", {
      params: { path: { slug } },
    }),
  );
}

/** The name the API gives the file (slug and date); the fallback only if a proxy drops it. */
function attachmentName(response: Response, fallback: string): string {
  const match = /filename="([^"]+)"/.exec(
    response.headers.get("Content-Disposition") ?? "",
  );
  return match?.[1] ?? fallback;
}

/**
 * Download the cell's suppression list as a file.
 *
 * A blob behind an `<a download>`, because the export is a POST with a reason in its body:
 * a plain link cannot carry one.
 */
export async function exportSuppressions(
  slug: string,
  reason: string,
): Promise<PlatformOutcome<string>> {
  const outcome = await write(`export ${slug}`, async () => {
    const { data, error, response } = await client.POST(
      "/api/v1/platform/cells/{slug}/suppressions/export",
      { params: { path: { slug } }, body: { reason }, parseAs: "blob" },
    );
    // An empty list is a 200 with `Content-Length: 0`, which the client reports as no data.
    const blob =
      data ??
      (response.ok
        ? new Blob([], { type: "application/x-ndjson" })
        : undefined);
    return {
      data:
        blob === undefined
          ? undefined
          : {
              blob,
              name: attachmentName(response, `sperrliste-${slug}.ndjson`),
            },
      error,
      response,
    };
  });
  if (!outcome.ok) {
    return outcome;
  }
  const url = URL.createObjectURL(outcome.data.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = outcome.data.name;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next tick: some browsers read the URL only after `click` returns.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
  console.log("[platform] suppression list exported", slug);
  return { ok: true, data: outcome.data.name };
}

/** Without `confirm` a dry run: the counts are real, nothing is written. */
export function applySuppressions(
  slug: string,
  body: SuppressionApplyRequest,
): Promise<PlatformOutcome<SuppressionApplication>> {
  return write(`apply ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/suppressions/apply", {
      params: { path: { slug } },
      body,
    }),
  );
}

export function seedCell(
  slug: string,
  drawEligible: boolean,
): Promise<PlatformOutcome<SeedResult>> {
  return write(`seed ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/seed", {
      params: { path: { slug } },
      body: { draw_eligible: drawEligible },
    }),
  );
}

export function deleteCell(
  slug: string,
  confirmSlug: string,
  reason: string,
): Promise<PlatformOutcome<ProvisioningReport>> {
  return write(`delete ${slug}`, () =>
    client.POST("/api/v1/platform/cells/{slug}/delete", {
      params: { path: { slug } },
      body: { confirm_slug: confirmSlug, reason },
    }),
  );
}

/** Same reasoning as the backoffice's `signOut`: reload even when the request failed. */
export async function platformSignOut(): Promise<void> {
  try {
    await client.POST("/api/v1/platform/logout", {});
  } catch (error: unknown) {
    console.error("[platform] sign-out failed", error);
  }
  window.location.assign("/?abgemeldet=1");
}
